import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRememberedState, useRememberedScroll } from '../../hooks/useRemembered';
import { Customer, InvoiceDatevConfig, QuoteConfig } from '../../types';
import { fetchCustomers, archiveCustomer, restoreCustomer, fetchCustomerStats } from '../../services/crmService';
import { CustomerStats } from '../../services/customerStats';
import { formatCents } from '../../services/money';
import { Button } from '../Button';
import {
  Users, Search, Plus, Loader2, AlertTriangle, RefreshCw, X,
  Building2, Mail, Phone, MapPin, ChevronRight, Download, Archive, ArchiveRestore, GitMerge, CheckCircle2, Upload, FileDown
} from 'lucide-react';
import { CustomerRatingStars } from './CustomerRatingStars';
import { CustomerDetail, CustomerDetailTab } from './CustomerDetail';
import { recall, remember } from '../../services/viewMemory';
import { CustomerImportModal } from './CustomerImportModal';
import { CustomerListImportModal } from './CustomerListImportModal';
import { CustomerOnboardingWizard } from './CustomerOnboardingWizard';
import { GeocodeBackfillModal } from './GeocodeBackfillModal';
import { NewCustomerDialog } from './NewCustomerDialog';
import { MergeCustomersDialog } from './MergeCustomersDialog';
import { exportCustomersDatevCsv, exportCustomersDatevExtf } from '../../services/customerListExport';
import { rechtFehlerOder, rechtFehltHinweis } from '../../services/rechte/rechtFehler';
import { useVertriebRechte } from './useVertriebRechte';

/** Kunden je Seite der Liste. Fest — die Liste soll nicht endlos scrollen. */
const PAGE_SIZE = 50;

interface Props {
  onBack?: () => void;
  quoteConfig?: QuoteConfig;
  /**
   * Direkt in die Akte dieses Kunden springen (Sprungziel der CRM-Karte,
   * ERP-Plan Abschnitt 6: „Drilldown … → Sprung in CustomerDetail"). Wirkt
   * genau einmal je Wert; danach navigiert der Nutzer normal weiter.
   */
  initialCustomerId?: string;
  /**
   * Grundeinstellung "Zahlungsziel (Tage)" aus dem Rechnungsmodul. Steht nur
   * als Platzhalter im Kundenfeld: die Kundenakte soll zeigen, was gilt,
   * wenn dort nichts Abweichendes vereinbart ist.
   */
  defaultPaymentTermsDays?: number;
  /**
   * DATEV-Konfiguration aus dem Rechnungsmodul (invoiceConfig.datev) — dieselbe
   * Quelle, aus der der Buchungsstapel seine Berater- und Mandantennummer
   * zieht. Der Debitorenstamm MUSS dieselben Werte tragen, sonst ordnet die
   * Kanzlei die beiden Dateien nicht demselben Mandanten zu.
   */
  datevConfig?: InvoiceDatevConfig;
  /**
   * Spruenge aus der Historie der Kundenakte. Die Ansicht wechselt dabei;
   * der Rueckweg landet ueber RETURN_KEY wieder in derselben Akte.
   * onOpenProject meldet `false`, wenn das Projekt nicht aufging.
   */
  onOpenQuote?: (quoteId: string) => void;
  onOpenInvoice?: (invoiceId: string) => void;
  onOpenProject?: (project: { id: string; name: string; status: string }) => Promise<boolean | void> | boolean | void;
}

/**
 * Kundenakte, aus der in einen Beleg oder ein Projekt gesprungen wurde.
 * Die Ansicht wird dabei demontiert und vergaesse die offene Akte; beim
 * naechsten Aufbau wird sie einmal eingeloest und geht auf der Historie auf.
 */
const RETURN_KEY = 'crm.returnTo';

export const CrmView: React.FC<Props> = ({
  quoteConfig, initialCustomerId, defaultPaymentTermsDays, datevConfig,
  onOpenQuote, onOpenInvoice, onOpenProject,
}) => {
  // Rollensystem: die Kundenakte erreicht auch, wer nur Angebote schreibt.
  // Anlegen, Aendern, Zusammenfuehren und Importieren nur mit „Kunden
  // verwalten“; Spruenge nur in Ansichten, die die Wache oeffnet.
  const darf = useVertriebRechte();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useRememberedState('crm.search', '');
  const scrollRef = useRememberedScroll('crm.scroll');
  // Gemerkt wie Suche und Bildlauf: der Rueckweg aus der Kundenakte landet
  // auf derselben Seite. Zurueck auf Seite 1 nur bei neuer Suche/Ansicht.
  const [page, setPage] = useRememberedState('crm.page', 1);
  const listTopRef = useRef<HTMLDivElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Neuanlage samt Dublettenpruefung: NewCustomerDialog.
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isListImportOpen, setIsListImportOpen] = useState(false);
  // Kombinierte Uebernahme: Projekte + Kanzleiliste in EINEM Abgleich.
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  // Sammellauf Verortung (Koordinaten fuer die Kundenkarte, ERP-Plan Phase 0).
  const [isGeocodeOpen, setIsGeocodeOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Archiv-Ansicht: archivierte Kunden bleiben abrufbar (nie geloescht),
  // stoeren aber die Arbeitsliste nicht.
  const [showArchived, setShowArchived] = useRememberedState('crm.archived', false);
  const [stats, setStats] = useState<Map<string, CustomerStats>>(new Map());

  // Zusammenfuehren: Quelle aus der Zeile, Ziel im Dialog.
  const [mergeSource, setMergeSource] = useState<Customer | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async (includeArchived = showArchived) => {
    setLoading(true);
    setError(null);
    try {
      const [data, statsData] = await Promise.all([
        fetchCustomers({ includeArchived }),
        fetchCustomerStats(),
      ]);
      setCustomers(data);
      setStats(statsData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunden konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(showArchived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  // Sprungziel aus der CRM-Karte: sobald der Kundenstamm geladen ist, die
  // gewuenschte Akte oeffnen. Bewusst an `customers` gehaengt und nicht an ein
  // eigenes Laden — der Kunde muss in der Liste stehen, sonst ist er archiviert
  // oder geloescht, und dann ist die Liste die richtige Antwort.
  const gesprungenRef = useRef<string | null>(null);
  // Rueckkehr aus Beleg/Projekt (RETURN_KEY) — beim Aufbau EINMAL gelesen.
  const rueckkehrRef = useRef(recall<{ customerId: string }>(RETURN_KEY) ?? null);
  const [detailTab, setDetailTab] = useState<CustomerDetailTab | undefined>(undefined);
  useEffect(() => {
    if (loading) return;
    const rueckkehr = rueckkehrRef.current;
    if (rueckkehr) {
      rueckkehrRef.current = null;
      remember(RETURN_KEY, undefined);
      // Ein Kartensprung ist eine NEUE Absicht und geht vor. App loescht
      // dessen Id beim Verlassen der Kunden — steht sie beim Aufbau, ist sie
      // frisch. Ohne sie ist die Marke der Rueckweg aus Beleg oder Projekt.
      const ziel = initialCustomerId ? undefined : customers.find((c) => c.id === rueckkehr.customerId);
      if (ziel) {
        setDetailTab('historie');
        setSelectedCustomer(ziel);
        return;
      }
    }
    if (!initialCustomerId || gesprungenRef.current === initialCustomerId) return;
    const treffer = customers.find((c) => c.id === initialCustomerId);
    if (!treffer) return;
    gesprungenRef.current = initialCustomerId;
    setDetailTab(undefined);
    setSelectedCustomer(treffer);
  }, [initialCustomerId, customers, loading]);

  /** Akte merken, dann springen. Geht das Projekt nicht auf, bleibt man hier. */
  const springeAusAkte = (customerId: string, sprung: () => Promise<boolean | void> | boolean | void) => {
    remember(RETURN_KEY, { customerId });
    void Promise.resolve(sprung()).then((ok) => {
      if (ok === false) remember(RETURN_KEY, undefined);
    });
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.customerNumber || '').toLowerCase().includes(q) ||
      (c.address || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [customers, searchTerm]);

  // Seite gekappt statt per Effekt zurueckgesetzt: ein Effekt liefe auch beim
  // Aufbau und loeschte die gemerkte Seite. Schrumpft die Liste (Archivieren,
  // Zusammenfuehren), steht man so auf der letzten statt einer leeren Seite.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), pageCount);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = useMemo(
    () => filtered.slice(pageStart, pageStart + PAGE_SIZE),
    [filtered, pageStart],
  );

  const goToPage = (n: number) => {
    setPage(n);
    listTopRef.current?.scrollIntoView({ block: 'start' });
  };

  const handleArchive = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await archiveCustomer(deleteTarget.id);
      if (selectedCustomer?.id === deleteTarget.id) setSelectedCustomer(null);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      alert('Archivieren fehlgeschlagen: ' + rechtFehlerOder(e));
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (c: Customer) => {
    try {
      await restoreCustomer(c.id);
      await load();
    } catch (e) {
      alert('Wiederherstellen fehlgeschlagen: ' + rechtFehlerOder(e));
    }
  };

  /** Datei-Download aus einem CSV-Text (Blob-URL wird sofort wieder frei). */
  const downloadCsv = (csv: string, fileName: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Lokales Datum (sv-SE liefert YYYY-MM-DD): toISOString waere UTC und
  // truege in Deutschland bis 01:00/02:00 nachts das Datum von gestern.
  const exportDate = () => new Date().toLocaleDateString('sv-SE');

  /** Export folgt der aktuellen Ansicht: Archivierte nur im Archiv-Modus. */
  const handleExport = () => {
    downloadCsv(exportCustomersDatevCsv(customers), `debitoren-${exportDate()}.csv`);
  };

  /**
   * Ohne DATEV-Konfiguration hat die EXTF-Kopfzeile keine Berater- und
   * Mandantennummer, und der Export bricht ab. Der Knopf wird darum GESPERRT
   * statt beim Klick zu scheitern: ein Knopf, der aussieht als ginge er, laesst
   * den Nutzer den Fehler bei sich suchen statt in den Einstellungen.
   *
   * Die Steuerberater-Liste daneben (exportCustomersDatevCsv) braucht keine
   * Konfiguration und bleibt darum offen.
   */
  const datevReady = Boolean(datevConfig?.consultantNumber && datevConfig?.clientNumber);

  /**
   * DATEV-EXTF: das Format, in dem die Kanzlei ihre eigene Debitorenliste
   * liefert — Privatpersonen mit getrenntem Vor-/Nachnamen, Firmen im
   * Unternehmens-Namensfeld.
   */
  const handleExportExtf = () => {
    const stamp = new Date().toLocaleDateString('sv-SE').replace(/-/g, '');
    try {
      // Der Wurf aus der Erzeugung bleibt gefangen, obwohl der Knopf schon
      // sperrt: die Sperre sieht nur, OB etwas eingetragen ist. Eine
      // Beraternummer mit Semikolon oder Buchstaben faellt erst im Waechter der
      // Kopfzeile auf, und das darf nicht still im Nichts enden — sonst laedt
      // der Nutzer eine Datei herunter, die die Kanzlei zurueckweist.
      // Kunden ohne brauchbares Debitorenkonto stehen nicht in der Datei. Das
      // bricht den Export bewusst nicht ab — der Rest der Liste ist gut —, darf
      // aber nicht still bleiben: die Kanzlei bekaeme sonst eine vollstaendig
      // aussehende Liste, in der einzelne Konten fehlen.
      const skipped: string[] = [];
      downloadCsv(
        exportCustomersDatevExtf(customers, datevConfig, new Date(), {
          onSkipped: (message) => skipped.push(message),
        }),
        `EXTF_Debitoren_${stamp}.csv`,
      );
      if (skipped.length > 0) {
        alert(
          `${skipped.length} Kunde(n) stehen NICHT in der Datei:\n\n` + skipped.join('\n\n'),
        );
      }
    } catch (e) {
      alert('DATEV-Export nicht möglich: ' + rechtFehlerOder(e));
    }
  };

  const handleDetailChange = (updated: Customer) => {
    setCustomers((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelectedCustomer(updated);
  };

  // Detailansicht
  if (selectedCustomer) {
    return (
      <CustomerDetail
        customer={selectedCustomer}
        onChange={handleDetailChange}
        onBack={() => { setSelectedCustomer(null); setDetailTab(undefined); }}
        quoteConfig={quoteConfig}
        defaultPaymentTermsDays={defaultPaymentTermsDays}
        initialTab={detailTab}
        readOnly={!darf.kunden}
        onOpenQuote={onOpenQuote && darf.ansicht('quotes') ? ((id) => springeAusAkte(selectedCustomer.id, () => onOpenQuote(id))) : undefined}
        onOpenInvoice={onOpenInvoice && darf.ansicht('invoices') ? ((id) => springeAusAkte(selectedCustomer.id, () => onOpenInvoice(id))) : undefined}
        onOpenProject={onOpenProject && ((p) => springeAusAkte(selectedCustomer.id, () => onOpenProject(p)))}
      />
    );
  }

  return (
    <div ref={scrollRef} data-glass-view="crm" className="h-full overflow-y-auto">
    <div className="space-y-6 max-w-7xl mx-auto animate-fade-in p-4 md:p-6 pb-20">
      {/* Header */}
      <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col lg:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-4 w-full lg:w-auto">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
            <Users className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Kunden</h2>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{customers.length} Kunden erfasst</p>
          </div>
        </div>
        <div className="flex gap-3 w-full lg:w-auto justify-end">
          <Button variant="secondary" onClick={handleExport} disabled={customers.length === 0} icon={<FileDown className="w-4 h-4" />}>
            Exportieren
          </Button>
          <Button
            variant="secondary"
            onClick={handleExportExtf}
            disabled={customers.length === 0 || !datevReady}
            icon={<FileDown className="w-4 h-4" />}
            title={datevReady
              ? 'DATEV-EXTF „Debitoren/Kreditoren“ — Format der Steuerkanzlei'
              : 'Beraternummer und Mandantennummer fehlen. Firmeneinstellungen → Rechnungen → Steuerberater-Export (DATEV / Simba). Ohne sie ordnet die Kanzlei den Debitorenstamm keinem Mandanten zu.'}
          >
            DATEV EXTF
          </Button>
          {darf.kunden && (
            <>
              <Button
                variant="secondary"
                onClick={() => setIsOnboardingOpen(true)}
                icon={<GitMerge className="w-4 h-4" />}
                title="Unternehmen aus den Projekten und die Kundenliste der Steuerkanzlei zusammen abgleichen"
              >
                Kunden übernehmen…
              </Button>
              <Button
                variant="secondary"
                onClick={() => setIsGeocodeOpen(true)}
                icon={<MapPin className="w-4 h-4" />}
                title="Koordinaten für die Kundenkarte nachtragen — von Hand gesetzte bleiben unberührt"
              >
                Adressen verorten
              </Button>
              <Button variant="secondary" onClick={() => setIsListImportOpen(true)} icon={<Upload className="w-4 h-4" />}>
                Kundenliste hochladen
              </Button>
              <Button variant="secondary" onClick={() => setIsImportOpen(true)} icon={<Download className="w-4 h-4" />}>
                Aus Projekten importieren
              </Button>
              <Button variant="primary" onClick={() => setIsCreateOpen(true)} className="shadow-lg border-b-2 border-blue-700" icon={<Plus className="w-4 h-4" />}>
                Neuer Kunde
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Nur-Ansicht (Rollensystem): die Akte sieht auch, wer nur Angebote
          schreibt. Anlegen, Aendern und Zusammenfuehren schreiben `customers`
          und brauchen „Kunden verwalten“ — die Datenbank prueft dasselbe. */}
      {!darf.kunden && (
        <div className="bg-slate-50 border border-slate-200 text-slate-600 rounded-2xl p-3 text-xs">
          Nur-Ansicht — {rechtFehltHinweis('kunden.verwalten')}.
        </div>
      )}

      {/* Suche + Archiv-Toggle */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row gap-3 items-center">
        <div className="relative w-full group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
          <input
            type="text"
            placeholder="Suchen nach Name, Adresse oder E-Mail…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 shrink-0 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => { setShowArchived(e.target.checked); setPage(1); }}
            className="w-4 h-4 rounded border-slate-300 text-blue-600"
          />
          Archiv anzeigen
        </label>
      </div>

      {/* Erfolgsleiste (z. B. nach Zusammenfuehren) */}
      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl p-4 text-sm flex items-start justify-between gap-3">
          <span className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />{notice}</span>
          <button onClick={() => setNotice(null)} className="shrink-0 hover:bg-emerald-100 rounded p-1"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Inhalt */}
      {loading ? (
        <div className="py-24 text-center text-slate-400 bg-white rounded-3xl border border-slate-200">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
          <p className="text-sm font-medium">Kunden werden geladen…</p>
        </div>
      ) : error ? (
        <div className="py-16 text-center bg-white rounded-3xl border border-red-100">
          <div className="bg-red-50 p-4 rounded-full w-fit mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <p className="font-bold text-slate-700">Fehler beim Laden</p>
          <p className="text-xs text-slate-500 mt-1 mb-4">{error}</p>
          {/* Nicht `onClick={load}`: React reicht das Klick-Ereignis als ersten
              Parameter durch, und `includeArchived` waere damit immer wahr —
              der Wiederholungsversuch haette stets das Archiv mitgeladen. */}
          <Button variant="secondary" onClick={() => { void load(); }} icon={<RefreshCw className="w-4 h-4" />}>Erneut versuchen</Button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-slate-400 bg-white rounded-3xl border-2 border-dashed border-slate-100">
          <div className="bg-slate-50 p-4 rounded-full w-fit mx-auto mb-4">
            <Users className="w-10 h-10 opacity-20" />
          </div>
          <p className="font-bold">{searchTerm ? 'Keine Kunden gefunden' : 'Noch keine Kunden angelegt'}</p>
          <p className="text-xs">{searchTerm ? 'Versuchen Sie einen anderen Suchbegriff.' : darf.kunden ? 'Legen Sie Ihren ersten Kunden an.' : ''}</p>
        </div>
      ) : (
        <div ref={listTopRef} className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden scroll-mt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[760px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-bold text-slate-500 tracking-wider">
                  <th className="px-5 py-4 w-[24%]">Name</th>
                  <th className="px-5 py-4 w-[20%]">Adresse</th>
                  <th className="px-5 py-4 w-[18%]">Kontakt</th>
                  <th className="px-5 py-4 text-right w-[12%]" title="Summe aller ausgestellten Belege, inkl. zusammengeführter Kunden">Umsatz</th>
                  <th className="px-5 py-4 text-center w-[8%]">Projekte</th>
                  <th className="px-5 py-4 w-[12%]">Bewertung</th>
                  <th className="px-3 py-4 text-right w-[70px]">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((c) => {
                  const s = stats.get(c.id);
                  const mergedTarget = c.mergedIntoId
                    ? customers.find((x) => x.id === c.mergedIntoId)
                    : undefined;
                  return (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedCustomer(c)}
                    className={`hover:bg-blue-50/40 transition-colors group cursor-pointer ${c.archivedAt ? 'opacity-60' : ''}`}
                  >
                    <td className="px-5 py-4 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0 group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">
                          <Building2 className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 truncate">{c.name || 'Unbenannt'}</span>
                            {c.customerNumber && <span className="text-[11px] text-slate-400 font-mono shrink-0">{c.customerNumber}</span>}
                            {c.archivedAt && (
                              <span className="text-[11px] font-bold uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5 shrink-0">Archiviert</span>
                            )}
                          </div>
                          {mergedTarget && (
                            <span className="block text-[11px] text-slate-400 truncate">zusammengeführt mit {mergedTarget.name}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 align-middle text-sm text-slate-500">
                      {c.address ? (
                        <span className="flex items-center gap-1.5 truncate">
                          <MapPin className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                          <span className="truncate">{c.address}</span>
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-4 align-middle text-xs text-slate-500">
                      <div className="space-y-1">
                        {c.phone && <span className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-slate-300" />{c.phone}</span>}
                        {c.email && <span className="flex items-center gap-1.5 truncate"><Mail className="w-3 h-3 text-slate-300" /><span className="truncate">{c.email}</span></span>}
                        {!c.phone && !c.email && <span className="text-slate-300">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4 align-middle text-right text-sm font-bold text-slate-700 font-mono whitespace-nowrap">
                      {s?.revenueCents ? formatCents(s.revenueCents) : <span className="text-slate-300 font-sans font-normal">—</span>}
                    </td>
                    <td className="px-5 py-4 align-middle text-center text-sm text-slate-600">
                      {s?.projectCount || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-4 align-middle">
                      <CustomerRatingStars value={c.ratingOverall} readOnly size="sm" />
                    </td>
                    <td className="px-3 py-4 align-middle text-right">
                      <div className="flex items-center justify-end gap-1">
                        {darf.kunden && !c.archivedAt && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setMergeSource(c); }}
                              className="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100"
                              title="Mit anderem Kunden zusammenführen"
                            >
                              <GitMerge className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                              className="p-2 text-slate-300 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100"
                              title="Kunde archivieren"
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {darf.kunden && c.archivedAt && !c.mergedIntoId && (
                          <button
                            onClick={(e) => { e.stopPropagation(); void handleRestore(c); }}
                            className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                            title="Wiederherstellen"
                          >
                            <ArchiveRestore className="w-4 h-4" />
                          </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 text-xs text-slate-500">
              <span className="tabular-nums">
                {pageStart + 1}–{pageStart + pageRows.length} von {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="rounded-lg border border-slate-200 px-3 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                >
                  Zurück
                </button>
                <span className="tabular-nums">
                  Seite {currentPage} von {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= pageCount}
                  className="rounded-lg border border-slate-200 px-3 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Anlegen-Modal */}
      {isCreateOpen && (
        <NewCustomerDialog
          onCreated={async (created) => {
            setIsCreateOpen(false);
            await load();
            setSelectedCustomer(created);
          }}
          onUseExisting={async (c, restored) => {
            setIsCreateOpen(false);
            if (restored) await load();
            setSelectedCustomer(c);
          }}
          onClose={() => setIsCreateOpen(false)}
        />
      )}

      {/* Zusammenfuehren-Dialog */}
      {mergeSource && (
        <MergeCustomersDialog
          source={mergeSource}
          customers={customers}
          onClose={() => setMergeSource(null)}
          onMerged={async (target) => {
            const src = mergeSource;
            setMergeSource(null);
            setNotice(
              `${src?.name} wurde mit ${target.name} zusammengeführt.` +
                (src?.customerNumber
                  ? ` Kundennummer ${src.customerNumber} bleibt am archivierten Kunden.`
                  : ''),
            );
            await load();
          }}
        />
      )}

      {/* Import-Modal */}
      {isImportOpen && (
        <CustomerImportModal
          onClose={() => setIsImportOpen(false)}
          onImported={load}
        />
      )}

      {/* Kombinierte Uebernahme: Projekte + Kanzleiliste mit Abgleich */}
      {isOnboardingOpen && (
        <CustomerOnboardingWizard
          onClose={() => setIsOnboardingOpen(false)}
          onImported={load}
        />
      )}

      {/* Kundenlisten-Upload (CSV/Excel/PDF) */}
      {isListImportOpen && (
        <CustomerListImportModal
          onClose={() => setIsListImportOpen(false)}
          onImported={load}
        />
      )}

      {/* Sammellauf Verortung (Nominatim + geo_cache, ERP-Plan Phase 0) */}
      {isGeocodeOpen && (
        <GeocodeBackfillModal
          onClose={() => setIsGeocodeOpen(false)}
          onFinished={load}
        />
      )}

      {/* Archivieren-Dialog: Loeschen gibt es nicht mehr — Rechnungen und
          Kundennummer bleiben dauerhaft (GoBD), und genau das steht hier. */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-amber-50 p-3 rounded-full shrink-0"><Archive className="w-6 h-6 text-amber-600" /></div>
              <div>
                <h3 className="font-bold text-lg text-slate-800">Kunde archivieren?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  <strong>{deleteTarget.name || 'Dieser Kunde'}</strong> wird archiviert, nicht gelöscht:
                  Rechnungen{deleteTarget.customerNumber ? <> und die Kundennummer <strong>{deleteTarget.customerNumber}</strong></> : null} bleiben
                  dauerhaft erhalten (GoBD). Der Kunde verschwindet aus der Übersicht
                  und lässt sich jederzeit wiederherstellen.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
              <Button variant="primary" onClick={handleArchive} disabled={deleting} icon={deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}>
                Archivieren
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};
